import { getClientPlatformHeader } from './deviceHeaders'
import { getWebSocketAuthToken } from '@shared/utils'

const getSameOriginWsUrl = (path: string) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}${path}`
}

export const withClientPlatformQuery = (url: string) => {
  try {
    const nextUrl = new URL(url)
    nextUrl.searchParams.set('platform', getClientPlatformHeader())
    return nextUrl.toString()
  } catch {
    return url
  }
}

export const getWebSocketProtocols = () => {
  const token = getWebSocketAuthToken()
  if (!token) return undefined

  return ['bearer', encodeURIComponent(token)]
}

const resolveWsUrl = (configuredUrl: string | undefined, path: string) => {
  if (!configuredUrl) return getSameOriginWsUrl(path)

  const url = new URL(configuredUrl)
  const isLocalhostConfig = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
  const isLocalhostPage = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  if (isLocalhostConfig && !isLocalhostPage) {
    return `${url.protocol}//${window.location.host}${url.pathname}${url.search}`
  }

  return configuredUrl
}

const DEFAULT_WS_URL = resolveWsUrl(import.meta.env.VITE_WS_BASE_URL, '/ws')

interface BaseSocketOptions<TOutMsg> {
  onOpen?: () => void
  prepareUrl?: (url: string) => string | Promise<string>
  useAuthProtocols?: boolean
  getReconnectMessages?: () => TOutMsg[]
  reconnect?: {
    enabled?: boolean
    initialDelayMs?: number
    maxDelayMs?: number
  }
}

const DEFAULT_RECONNECT_INITIAL_DELAY_MS = 1000
const DEFAULT_RECONNECT_MAX_DELAY_MS = 30000

export const createBaseSocket = <
  TOutMsg,
  TListener extends (...args: never[]) => void
>(
  onMessage: (raw: string, listeners: Set<TListener>) => void,
  defaultUrl: string = DEFAULT_WS_URL,
  optionsOrOnOpen?: (() => void) | BaseSocketOptions<TOutMsg>,
) => {
  const options =
    typeof optionsOrOnOpen === 'function' ? { onOpen: optionsOrOnOpen } : optionsOrOnOpen ?? {}
  const reconnectOptions = options.reconnect ?? {}
  const reconnectEnabled = reconnectOptions.enabled ?? true
  const reconnectInitialDelayMs =
    reconnectOptions.initialDelayMs ?? DEFAULT_RECONNECT_INITIAL_DELAY_MS
  const reconnectMaxDelayMs = reconnectOptions.maxDelayMs ?? DEFAULT_RECONNECT_MAX_DELAY_MS

  let ws: WebSocket | null = null
  let currentUrl = defaultUrl
  let isOpening = false
  let shouldReconnect = false
  let reconnectAttempts = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let hasOpened = false
  let connectRequestId = 0
  const listeners = new Set<TListener>()
  const pendingMessages: string[] = []

  const clearReconnectTimer = () => {
    if (!reconnectTimer) return
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }

  const sendJson = (json: string) => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(json)
      return true
    }
    return false
  }

  const scheduleReconnect = () => {
    if (!reconnectEnabled || !shouldReconnect || reconnectTimer) return

    const delay = Math.min(
      reconnectInitialDelayMs * 2 ** reconnectAttempts,
      reconnectMaxDelayMs,
    )
    reconnectAttempts += 1

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect(currentUrl)
    }, delay)
  }

  const openSocket = (url: string) => {
    const protocols = options.useAuthProtocols === false ? undefined : getWebSocketProtocols()
    ws = protocols
      ? new WebSocket(withClientPlatformQuery(url), protocols)
      : new WebSocket(withClientPlatformQuery(url))

    const closeCurrentSocket = () => {
      ws?.close()
    }

    ws.onopen = () => {
      const isReconnect = hasOpened
      hasOpened = true
      reconnectAttempts = 0
      for (const msg of pendingMessages) {
        sendJson(msg)
      }
      pendingMessages.length = 0
      options.onOpen?.()
      if (isReconnect) {
        for (const msg of options.getReconnectMessages?.() ?? []) {
          sendJson(JSON.stringify(msg))
        }
      }
    }

    ws.onmessage = (event: MessageEvent) => {
      const raw = event.data as string
      try {
        const parsed = JSON.parse(raw) as { code?: string }
        if (parsed.code === 'AUTH_FAILED') {
          closeCurrentSocket()
          return
        }
      } catch {
        // 도메인별 onMessage에서 처리한다.
      }

      onMessage(raw, listeners)
    }

    ws.onerror = () => {
      // 소켓 오류 — 재연결은 훅 레이어에서 담당
    }

    ws.onclose = () => {
      ws = null
      scheduleReconnect()
    }
  }

  const connect = (url: string = defaultUrl) => {
    currentUrl = url
    shouldReconnect = true
    clearReconnectTimer()

    if (
      isOpening ||
      (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
    ) {
      return
    }

    const requestId = ++connectRequestId
    const preparedUrl = options.prepareUrl?.(url) ?? url

    if (typeof preparedUrl === 'string') {
      openSocket(preparedUrl)
      return
    }

    isOpening = true
    preparedUrl
      .then(nextUrl => {
        isOpening = false
        if (!shouldReconnect || requestId !== connectRequestId) return
        openSocket(nextUrl)
      })
      .catch(() => {
        isOpening = false
        if (!shouldReconnect || requestId !== connectRequestId) return
        scheduleReconnect()
      })
  }

  const disconnect = () => {
    shouldReconnect = false
    hasOpened = false
    isOpening = false
    connectRequestId += 1
    clearReconnectTimer()
    if (ws) {
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null
      ws.close()
      ws = null
    }
    pendingMessages.length = 0
  }

  const send = (msg: TOutMsg) => {
    const json = JSON.stringify(msg)
    if (!sendJson(json)) {
      pendingMessages.push(json)
    }
  }

  const addListener = (fn: TListener): (() => void) => {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }

  const removeListener = (fn: TListener) => {
    listeners.delete(fn)
  }

  return { connect, disconnect, send, addListener, removeListener }
}
